import type {BBOX, HEXColor, SupportedLanguage} from 'talptech-gis-components';

import parse4326Bbox from './parse4326Bbox';
import parseBoolean from './parseBoolean';
import parseCategories from './parseCategories';
import parseColorBreaks from './parseColorBreaks';

type Values = {
  cogUrl: string;
  bbox?: BBOX;
  legendTitle?: string;
  lang?: SupportedLanguage;
  quantitativeLegend?: {
    thresholds: Array<number>;
    colors: Array<HEXColor>;
    min?: number;
    max?: number;
    isDiscrete?: boolean;
  };
  categoricalLegend?: Array<{
    code: string;
    value: number;
    color: HEXColor;
  }>;
};

const parseCogScreenshotQueryParameters = (href: string): Values => {
  // Get this page's query parameters
  const searchParams = new URLSearchParams(new URL(href).search).entries();
  const {
    cogUrl,
    bbox,
    legendTitle,
    colorBreaks,
    categories,
    min: strMin,
    max: strMax,
    lang,
    discrete,
  } = Object.fromEntries(searchParams);

  if (!cogUrl) {
    throw new Error('"cogUrl" GET parameter is mandatory');
  }

  if (!colorBreaks && !categories) {
    throw new Error('Either "colorBreaks" or "categories" GET parameter is mandatory');
  }

  const values: Values = {
    cogUrl,
    legendTitle,
  };

  if (bbox) {
    values.bbox = parse4326Bbox(bbox);
  }

  if (lang && (lang === 'es' || lang === 'en')) {
    values.lang = lang;
  }

  if (colorBreaks) {
    const {thresholds, colors} = parseColorBreaks(colorBreaks);
    const [min, max] = [strMin, strMax].map((str) => (str && !Number.isNaN(Number(str)) ? Number(str) : undefined));
    const isDiscrete = parseBoolean(discrete);
    values.quantitativeLegend = {thresholds, colors, min, max, isDiscrete};
  }

  if (categories) {
    values.categoricalLegend = parseCategories(categories);
  }

  return values;
};

export default parseCogScreenshotQueryParameters;
